/* SupplierContext — the workspace-switcher state for the Buyer portal.
   When `active` is a tenant object, the portal re-skins (logo + colors) and
   every page filters its data to that seller. When null, the buyer is in the
   unified verqo hub.

   Persisted to localStorage so the scope survives refreshes. */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const STORAGE_KEY = "verqo_active_supplier_slug";
const SupplierCtx = createContext(null);

export function SupplierProvider({ children }) {
  const [connections, setConnections] = useState([]);
  const [invites, setInvites] = useState([]);
  const [active, setActive] = useState(null);        // tenant object or null
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [c, i] = await Promise.all([
        api.get("/buyer/connections").catch(() => ({ data: [] })),
        api.get("/buyer/invites/pending").catch(() => ({ data: [] })),
      ]);
      setConnections(c.data || []);
      setInvites(i.data || []);
      // Restore active from localStorage (if still connected). If no saved
      // scope and the buyer has exactly one supplier, auto-select it so the
      // portal isn't permanently stuck in Hub mode for single-supplier
      // accounts (the original verqo demo persona is exactly this case).
      const savedSlug = localStorage.getItem(STORAGE_KEY);
      const conns = c.data || [];
      if (savedSlug) {
        const hit = conns.find(x => x.tenant_slug === savedSlug);
        if (hit) setActive({ ...hit, branding: hit.branding || {} });
        else { localStorage.removeItem(STORAGE_KEY); setActive(null); }
      } else if (conns.length === 1) {
        const only = conns[0];
        localStorage.setItem(STORAGE_KEY, only.tenant_slug);
        setActive({ ...only, branding: only.branding || {} });
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const selectSupplier = useCallback((slugOrConnection) => {
    if (!slugOrConnection) {
      localStorage.removeItem(STORAGE_KEY);
      setActive(null);
      return;
    }
    const slug = typeof slugOrConnection === "string" ? slugOrConnection : slugOrConnection.tenant_slug;
    const hit = connections.find(x => x.tenant_slug === slug);
    if (hit) {
      localStorage.setItem(STORAGE_KEY, slug);
      setActive({ ...hit, branding: hit.branding || {} });
    }
  }, [connections]);

  const clearSupplier = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setActive(null);
  }, []);

  const acceptInvite = useCallback(async (token) => {
    const { data } = await api.post(`/invites/accept/${token}`);
    await refresh();
    // Switch to the newly-connected supplier
    localStorage.setItem(STORAGE_KEY, data.tenant_slug);
    // Wait a tick for refresh to complete, then re-hydrate active
    setTimeout(() => {
      const hit = (connections || []).find(x => x.tenant_slug === data.tenant_slug);
      if (hit) setActive({ ...hit, branding: hit.branding || {} });
    }, 100);
    return data;
  }, [connections, refresh]);

  return (
    <SupplierCtx.Provider value={{
      connections, invites, active,
      selectSupplier, clearSupplier, acceptInvite, refresh,
      loading,
      // Convenience: the effective seller name string to filter on (Mongo products are stored with seller_name)
      activeSellerName: active?.tenant_name || null,
      // Stable seller user_id to filter records by — preferred over name-matching.
      // null when buyer is in the hub (verqo · All suppliers).
      activeSellerId: active?.tenant_owner_user_id || null,
      // Helper: filters a list of items down to the active supplier (no-op in hub).
      // Tries multiple common keys so callers don't need to think.
      scopeToSupplier: (items, opts = {}) => {
        const sid = active?.tenant_owner_user_id;
        if (!sid || !Array.isArray(items)) return items;
        const keys = opts.keys || ["seller_id", "tenant_owner_user_id"];
        return items.filter(it => keys.some(k => it && it[k] === sid));
      },
    }}>
      {children}
    </SupplierCtx.Provider>
  );
}

export const useSupplier = () => useContext(SupplierCtx) || {
  connections: [], invites: [], active: null,
  selectSupplier: () => {}, clearSupplier: () => {}, acceptInvite: async () => {},
  refresh: async () => {}, loading: false,
  activeSellerName: null, activeSellerId: null,
  scopeToSupplier: (items) => items,
};
